/**
 * PR-CHECKOUT-ASYNC-RECOVERY-UX-V1
 *
 * Recovery bootstrap layer — re-fetches authoritative backend state when the
 * browser regains visibility or network connectivity.
 *
 * RULE 1: Backend runtime_state remains authoritative — never inferred from browser lifecycle.
 * RULE 4: Recovery always begins from an authoritative snapshot (LOAD_SESSION → LOADING_SESSION
 *         → authoritative fetch → categorizeState → canonical render).
 *
 * Triggers:
 *   - document:visibilitychange → visible  (tab switch-back, banking-app return, device wake)
 *   - window:online             (connectivity restore)
 *
 * Rate-limited: only fires if >3 s have elapsed since last trigger to absorb
 * rapid app-switch oscillation on iOS/Android.
 *
 * Does NOT:
 *   - Infer payment status from browser lifecycle events
 *   - Replay orchestration state
 *   - Set canonical runtime state
 */

import { useEffect, useRef } from 'react';

const RECOVERY_DEBOUNCE_MS = 3_000;

export interface UseCheckoutRecoveryOptions {
  /** Called when an authoritative re-fetch should be initiated. */
  onRecover: () => void;
}

/**
 * Registers recovery triggers (visibilitychange, online) and calls onRecover()
 * when the customer returns to the checkout after backgrounding or going offline.
 */
export function useCheckoutRecovery({ onRecover }: UseCheckoutRecoveryOptions): void {
  const onRecoverRef = useRef(onRecover);
  const lastRecoveryRef = useRef(0);

  useEffect(() => { onRecoverRef.current = onRecover; });

  useEffect(() => {
    const maybeRecover = () => {
      const now = Date.now();
      if (now - lastRecoveryRef.current < RECOVERY_DEBOUNCE_MS) return;
      lastRecoveryRef.current = now;
      onRecoverRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') maybeRecover();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', maybeRecover);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', maybeRecover);
    };
  }, []);
}
